import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, CardHeader, Collapse } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import MetaTags from 'react-meta-tags';
import { useAuth } from '../../store/auth';
import { AcMaintenanceContent } from "../../data";
import { AcMaintenanceContentArabic } from "../../data/indexArabic";

const AcMaintenance = () => {
    const { isArabic } = useAuth();
    const data = isArabic ? AcMaintenanceContent : AcMaintenanceContentArabic;

    // --- Modernized State Management ---
    const [activeSection, setActiveSection] = useState('');
    const [openAccordion, setOpenAccordion] = useState(null);

    // --- Data Structuring & Refs for Scrolling ---
    const sectionOrder = [
        'introduction', 'importanceOfMaintenance', 'typesOfMaintenance', 
        'maintenanceContracts', 'tipsForMaintenance', 'otherInquiries', 'faq'
    ];
    const sectionRefs = useRef(sectionOrder.reduce((acc, value) => {
        // Map simplified keys to the full data keys
        const dataKey = value === 'introduction' ? 'introductionTitle' : value;
        acc[dataKey] = React.createRef();
        return acc;
    }, {}));

    // --- Effects for Navbar & Scrollspy ---
    useEffect(() => {
        const handleGlobalNavScroll = () => {
            const topnav = document.getElementById("topnav");
            if (topnav) topnav.classList.toggle("nav-sticky", window.scrollY > 80);
        };
        const handleScrollSpy = () => {
            const currentPosition = window.scrollY + 150;
            let currentSectionId = '';
            sectionOrder.forEach(key => {
                const dataKey = key === 'introduction' ? 'introductionTitle' : key;
                const section = sectionRefs.current[dataKey]?.current;
                if (section && section.offsetTop <= currentPosition) {
                    currentSectionId = dataKey;
                }
            });
            if (currentSectionId !== activeSection) {
                setActiveSection(currentSectionId);
            }
        };
        window.addEventListener('scroll', handleGlobalNavScroll);
        window.addEventListener('scroll', handleScrollSpy);
        return () => {
            window.removeEventListener('scroll', handleGlobalNavScroll);
            window.removeEventListener('scroll', handleScrollSpy);
        };
    }, [activeSection]);

    // --- Event Handlers ---
    const handleNavClick = (event, sectionId) => {
        event.preventDefault();
        const targetElement = document.getElementById(sectionId);
        if (targetElement) {
            const navbarHeight = document.getElementById('topnav')?.offsetHeight || 80;
            const offsetPosition = targetElement.offsetTop - navbarHeight - 20;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    const toggleAccordion = (index) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    // --- Helper to get section titles ---
    const getSectionTitle = (key) => {
        if (key === 'introductionTitle') return data?.introductionTitle;
        return data[key]?.title;
    };
    
    return (
        <React.Fragment>
            <MetaTags>
                <title>AC Maintenance | Subul Contracting</title>
            </MetaTags>

            <section className="bg-half-170 bg-light d-table w-100">
                <Container>
                    <Row className="mt-5 justify-content-center">
                        <Col lg={12} className="text-center">
                            <div className="pages-heading">
                                <h1 className="title mb-0">{data?.title}</h1>
                            </div>
                        </Col>
                    </Row>
                    <div className="position-breadcrumb">
                        <nav aria-label="breadcrumb" className="d-inline-block">
                            <ul className="breadcrumb rounded shadow mb-0 px-4 py-2">
                                {data?.breadcrumb?.map((item, index) => (
                                    <li className={item?.className} key={index}>
                                        {item?.link ? <Link to={item.link}>{item.name}</Link> : item?.name}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </Container>
            </section>

            <div className="position-relative">
                <div className="shape overflow-hidden text-white">
                    <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
                    </svg>
                </div>
            </div>

            <section className="section">
                <Container>
                    <Row>
                        {/* Sidebar Navigation */}
                        <Col lg={4} md={5} className="d-none d-md-block">
                            <div className="position-sticky" style={{ top: '100px' }}>
                                <div className="content-nav">
                                    <ul className="nav nav-pills flex-column">
                                        {sectionOrder.map(key => {
                                            const dataKey = key === 'introduction' ? 'introductionTitle' : key;
                                            return (
                                                <li className="nav-item" key={dataKey}>
                                                    <a href={`#${dataKey}`} onClick={(e) => handleNavClick(e, dataKey)} className={`nav-link ${activeSection === dataKey ? 'active' : ''}`}>
                                                        {getSectionTitle(dataKey)}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </Col>

                        {/* Main Content */}
                        <Col lg={8} md={7}>
                            <div id="introductionTitle" ref={sectionRefs.current.introductionTitle} className="mb-5">
                                <h4 className="card-title mb-3">{data?.introductionTitle}</h4>
                                <p className="text-muted">{data?.introduction}</p>
                            </div>
                            
                            <div id="importanceOfMaintenance" ref={sectionRefs.current.importanceOfMaintenance} className="mb-5">
                                <h4 className="card-title mb-3">{data?.importanceOfMaintenance.title}</h4>
                                {data?.importanceOfMaintenance.points.map((point, index) => (
                                    <p className="text-muted" key={index}><b>{point.title}: </b>{point.text}</p>
                                ))}
                            </div>

                            <div id="typesOfMaintenance" ref={sectionRefs.current.typesOfMaintenance} className="mb-5">
                                <h4 className="card-title mb-3">{data?.typesOfMaintenance.title}</h4>
                                <p className="text-muted fw-bold">{data?.typesOfMaintenance.preventiveMaintenance.title}</p>
                                <ul className="list-unstyled feature-list text-muted">
                                    {data?.typesOfMaintenance.preventiveMaintenance.tasks.map((point, index) => (
                                        <li className="d-flex mb-2" key={index}><FeatherIcon icon="check-circle" className="fea icon-sm text-success me-2" /><span><b>{point.title}: </b>{point.text}</span></li>
                                    ))}
                                </ul>
                                <p className="text-muted fw-bold mt-4">{data?.typesOfMaintenance.emergencyMaintenance.title}</p>
                                <ul className="list-unstyled feature-list text-muted">
                                    {data?.typesOfMaintenance.emergencyMaintenance.tasks.map((point, index) => (
                                        <li className="d-flex mb-2" key={index}><FeatherIcon icon="check-circle" className="fea icon-sm text-success me-2" /><span><b>{point.title}: </b>{point.text}</span></li>
                                    ))}
                                </ul>
                            </div>

                            <div id="maintenanceContracts" ref={sectionRefs.current.maintenanceContracts} className="mb-5">
                                <h4 className="card-title mb-3">{data?.maintenanceContracts.title}</h4>
                                <p className="text-muted">{data?.maintenanceContracts.text}</p>
                                {data?.maintenanceContracts.details.map((point, index) => (
                                    <p className="text-muted" key={index}><b>{point.title}: </b>{point.text}</p>
                                ))}
                            </div>

                            <div id="tipsForMaintenance" ref={sectionRefs.current.tipsForMaintenance} className="mb-5">
                                <h4 className="card-title mb-3">{data?.tipsForMaintenance.title}</h4>
                                <p className="text-muted">{data?.tipsForMaintenance.text}</p>
                                <ul className="list-unstyled feature-list text-muted">
                                    {data?.tipsForMaintenance.tips.map((point, index) => (
                                        <li className="d-flex mb-2" key={index}><FeatherIcon icon="check-circle" className="fea icon-sm text-success me-2" />{point}</li>
                                    ))}
                                </ul>
                            </div>

                            <div id="otherInquiries" ref={sectionRefs.current.otherInquiries} className="mb-5">
                                <h4 className="card-title mb-3">{data?.otherInquiries.title}</h4>
                                <p className="text-muted">{data?.otherInquiries.text1}</p>
                                <p className="text-muted">{data?.otherInquiries.text2}</p>
                                <p className="text-muted">{data?.otherInquiries.text3}</p>
                            </div>

                            <div id="faq" ref={sectionRefs.current.faq} className="mb-5">
                                <h4 className="card-title mb-3">{data?.faq?.title}</h4>
                                <div className="accordion mt-4" id="accordionExample">
                                    {data?.faq?.faqItems?.map((faqItem, index) => (
                                        <Card key={index} className="border-0 rounded-3 shadow-sm mb-2 overflow-hidden">
                                            <Link to="#" onClick={(e) => { e.preventDefault(); toggleAccordion(index); }} className={`faq d-block position-relative ${openAccordion === index ? "text-primary" : "text-dark"}`}>
                                                <CardHeader className="border-0 bg-light p-3">
                                                    <h6 className="title mb-0 d-flex justify-content-between align-items-center">{faqItem?.question}<i className={`mdi ${openAccordion === index ? "mdi-chevron-up" : "mdi-chevron-down"}`}></i></h6>
                                                </CardHeader>
                                            </Link>
                                            <Collapse isOpen={openAccordion === index}>
                                                <CardBody><p className="text-muted mb-0 faq-ans">{faqItem?.answer}</p></CardBody>
                                            </Collapse>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
}

export default AcMaintenance;